import { useInstance } from "@meraki-internal/react-dependency-injection";
import { ClubState, IVolunteerMediaType } from "../clubs/ClubsState";
import { IonButton, IonInput, IonItem, IonList, IonTextarea, IonToggle } from "@ionic/react";
import { useState } from "react";
import { EditableImage } from "./EditableImage";
import { badgeEligibleTitles } from "./VolunteerCard";

const FieldInfo: React.FC<{ children: React.ReactNode}> = ({ children }) => {
    return (
        <p style={{ color: 'gray', fontSize: 10, lineHeight: 1, paddingBottom: 5 }}>
            <i className="fa-solid fa-circle-info" style={{ color: 'var(--ion-color-primary)', paddingRight: 10}} />
            {children}
        </p>
    )
}

export const VolunteerCardForm: React.FC<{ volunteer?: IVolunteerMediaType; onSaved: () => void; onCancel: () => void; }> = ({ onSaved, volunteer, onCancel }) => {
    const clubState = useInstance(ClubState);

    const [form, setForm] = useState<Partial<IVolunteerMediaType>>(volunteer || {});
    const [isUploading, setIsUploading] = useState(false);

    const save = async () => {
        if (volunteer){
            await clubState.updateVolunteer(form as IVolunteerMediaType);
        }
        else {
            await clubState.addVolunteer(form);
        }

        onSaved();
    }

    const cancel = () => {
        onCancel();
    }

    const isValid = (form.name || '').length > 0;

    return (
        <>
            <IonList>
                <IonItem>
                    <IonInput
                        label="Name"
                        labelPlacement="floating"
                        value={form.name}
                        onIonChange={e => setForm(val => ({
                            ...val,
                            name: e.detail.value || ''
                        }))}
                        placeholder="Lana Nelson"
                        autoFocus
                    />
                </IonItem>
                <IonItem>
                    <div>
                        <EditableImage
                            size={240}
                            src={form.imagePath}
                            onChange={imagePath => setForm(val => ({ ...val, imagePath }))}
                            onIsUploading={setIsUploading}
                            // TODO: losing changes here b/c not saving other fields on every key stroke
                        />
                        <FieldInfo>
                            The image will be scaled to 240 x 240 maintaining aspect ratio. There may be badges in any of the 4 corners so avoid your face being too close to the top.
                        </FieldInfo>
                    </div>
                </IonItem>
                <IonItem>
                    <div>
                        <IonToggle
                            checked={form.isClubAdmin}
                            onIonChange={e => {
                                setForm(val =>({
                                    ...val,
                                    isClubAdmin: e.detail.checked
                                }))
                            }}
                        >
                            Is Club Administrator
                        </IonToggle>
                        <FieldInfo>
                            Club admins have access to manage everything about the club. Currently there are only 2 roles, a random user with no permissions, or a club admin with full permissions to the club.
                        </FieldInfo>
                    </div>

                </IonItem>
                <IonItem>
                    <div>
                        <IonInput
                            label="Email"
                            labelPlacement="floating"
                            placeholder="lana@email.com"
                            value={form.email}
                            onIonChange={e => setForm(val => ({ ...val, email: e.detail.value || '' }))}
                        />
                        <FieldInfo>
                            This email address will only be visible to authenticated coaches with access to your club (at the moment, club admins). It will not be visible on the main site by random users. If the volunteer is a club admin, this is the email they have to login with.
                        </FieldInfo>
                    </div>

                </IonItem>
                <IonItem>
                    <div>
                        <IonTextarea
                            autoGrow
                            label="Title(s)"
                            labelPlacement="floating"
                            placeholder="Head Coach"
                            value={(form.titles || []).join('\n')}
                            onIonChange={e => setForm(val => ({ ...val, titles: (e.detail.value || '').split('\n').filter(line => Boolean(line)) }))}
                        />
                        <FieldInfo>
                            Enter one or more titles (one per line). The first title will get emphasis (bold) and should be your title as a volunteer (eg Eletronics Mentor, or Coach). Additionally you should list your professional title(s) to show the diversity of talent we bring to the robotics teams (even if it has nothing to do with Robotics).
                        </FieldInfo>
                        <FieldInfo>
                            The following titles will result in badges: {badgeEligibleTitles.join(", ")}
                        </FieldInfo>
                    </div>

                </IonItem>

            </IonList>
            <div style={{ display: 'flex', justifyContent: 'space-between', margin: 20}}>
                <IonButton style={{ flexGrow: 1 }} fill="outline" onClick={cancel}>Cancel</IonButton>
                <IonButton style={{ flexGrow: 1 }} fill="solid" onClick={save} disabled={!isValid || isUploading}>Save</IonButton>
            </div>
        </>
    )
}